import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { Avatar, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import moment from "moment";

import SAVIcon from "../IconComponents/SAVIcon";
import EditSAVTicket from "../../components/EditSAVTicket/EditSAVTicket";
import ViewSAVTicket from "../../Pages/SAV/TicketDetails/View/ViewSAVTicket";
import { getNotifications } from "../../ReduxTLK/features/Notifications/NotificationSlice";
import { resetEditTicketDraftByIndex } from "../../ReduxTLK/features/SAV/NewTicketDraftSlice";
import {
  addTab,
  removeTabById,
  setLastActiveTab,
} from "../../ReduxTLK/features/Tabs/TabsSlice";
import { NavContext } from "../../App";
import { Persistor } from "../..";

import styles from "./NotificationItemStack.module.css";

const PAGE_SIZE = 20;
const STATUS_CLOTURE = 6;
const STATUS_NOUVEAU = 0;

/* ---------- Temps relatif compact : 45 s · 12 m · 3 h · 5 j ---------- */
const getRelativeTime = (date) => {
  const seconds = moment().diff(moment(date), "seconds");
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} j`;
};

/* =========================================================================
   Carte de notification — un seul composant, l'état lu/non-lu est une prop
   ========================================================================= */
function NotificationCard({ notif, unread, onClick }) {
  const summary = notif?.contenu?.split(",")[0]?.trim();

  return (
    <button
      type="button"
      className={`${styles.card} ${unread ? styles.unread : styles.read}`}
      onClick={onClick}
    >
      <Avatar size={42} className={styles.avatar}>
        SAV
      </Avatar>

      <span className={styles.body}>
        <span className={styles.topRow}>
          <span className={styles.title}>{notif?.titre}</span>
          <span className={styles.time}>{getRelativeTime(notif?.date)}</span>
        </span>

        {summary && <span className={styles.summary}>{summary}</span>}

        <span className={styles.meta}>
          <span className={styles.metaItem}>{notif?.chaine}</span>
          {notif?.pv && (
            <>
              <span className={styles.metaDot} />
              <span className={styles.metaItem}>{notif.pv}</span>
            </>
          )}
        </span>
      </span>

      {unread && <span className={styles.unreadDot} aria-label="Nouvelle" />}
    </button>
  );
}

/* =========================================================================
   Liste avec scroll infini
   ========================================================================= */
export default function NotificationItemStack({ onClose }) {
  const dispatch = useDispatch();
  const context = useContext(NavContext);
  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [unseen, setUnseen] = useState([]);
  const [seen, setSeen] = useState([]);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const isFirstLoad = pageSize === PAGE_SIZE;
    if (!isFirstLoad) setLoadingMore(true);

    dispatch(getNotifications({ page: 1, pageSize }))
      .then(unwrapResult)
      .then((res) => {
        const all = res?.data?.data?.result?.notifications ?? [];
        const newCount = context.newNotification ?? 0;
        setUnseen(all.slice(0, newCount));
        setSeen(all.slice(newCount));
        setHasMore(all.length >= pageSize);
      })
      .catch((error) => console.error("Error fetching notifications:", error))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
        if (pageSize === PAGE_SIZE) context.setNewNotification(0);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore) return;
    const reachedBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <= 40;
    if (reachedBottom) setPageSize((size) => size + PAGE_SIZE);
  };

  /* ---------- Ouverture / activation d'onglet ticket ---------- */
  const activateTab = (tabId) => {
    context.setActiveTab(tabId);
    dispatch(setLastActiveTab(tabId));
  };

  const buildTabEntry = (notif, tabId, isView, extraProps = {}) => ({
    id: tabId,
    label: `#${notif.idTicket}`,
    identifier: notif.idTicket,
    component: isView ? (
      <ViewSAVTicket id={notif.idTicket} {...extraProps} />
    ) : (
      <EditSAVTicket id={notif.idTicket} {...extraProps} />
    ),
    closeable: true,
    icon: <SAVIcon />,
  });

  const registerTab = (notif, tabId, isView, tabs, extraProps) => {
    dispatch(
      addTab({
        module: "SAV",
        action: isView ? "View" : "Edit",
        identifier: notif.idTicket,
        id: tabId,
        label: `#${notif.idTicket}`,
      })
    );
    context.setTabs([...tabs, buildTabEntry(notif, tabId, isView, extraProps)]);
    Persistor.persist();
    activateTab(tabId);
  };

  /* Ticket clôturé : on remplace l'onglet existant par la vue lecture seule */
  const replaceWithViewTab = (notif, tabId) => {
    const remainingTabs = context.tabs.filter(
      (tab) => tab.identifier !== notif.idTicket
    );
    dispatch(removeTabById(tabId));
    registerTab(notif, tabId, true, remainingTabs);
  };

  const handleNotifClick = (notif) => {
    const tabId = `SAV_Ticket_${notif.idTicket}`;
    const tabExists = context.tabs.some((tab) => tab.id === tabId);

    if (tabExists) {
      if (context.activeTab === tabId) {
        context.setTabToBeReloadedNotif(notif.idTicket);
      } else {
        activateTab(tabId);
      }
      if (notif.statut === STATUS_CLOTURE) replaceWithViewTab(notif, tabId);
    } else {
      dispatch(resetEditTicketDraftByIndex(notif.idTicket));
      const isView =
        notif.statut === STATUS_CLOTURE || notif.statut === STATUS_NOUVEAU;
      registerTab(notif, tabId, isView, context.tabs);
    }

    onClose?.();
  };

  /* ---------- Rendu ---------- */
  if (loading) {
    return (
      <div className={styles.centerState}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 28 }} spin />} />
      </div>
    );
  }

  if (!unseen.length && !seen.length) {
    return (
      <div className={styles.centerState}>
        <span className={styles.emptyIcon}>
          <NotificationsNoneIcon sx={{ fontSize: 26 }} />
        </span>
        <p className={styles.emptyTitle}>Aucune notification</p>
        <p className={styles.emptyText}>Les nouvelles alertes apparaîtront ici.</p>
      </div>
    );
  }

  return (
    <div ref={scrollRef} onScroll={handleScroll} className={styles.scrollBox}>
      {unseen.length > 0 && (
        <>
          <div className={styles.sectionLabel}>Nouvelles</div>
          {unseen.map((notif) => (
            <NotificationCard
              key={notif.id ?? `${notif.idTicket}-${notif.date}`}
              notif={notif}
              unread
              onClick={() => handleNotifClick(notif)}
            />
          ))}
          {seen.length > 0 && <div className={styles.sectionLabel}>Plus anciennes</div>}
        </>
      )}

      {seen.map((notif) => (
        <NotificationCard
          key={notif.id ?? `${notif.idTicket}-${notif.date}`}
          notif={notif}
          onClick={() => handleNotifClick(notif)}
        />
      ))}

      {loadingMore && (
        <div className={styles.loadingMore}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 22 }} spin />} />
        </div>
      )}
    </div>
  );
}